import { RealtimeChannel } from '@supabase/supabase-js';
import { AlertTriangle, CheckCircle, FileText, TrendingUp } from 'lucide-react';
import posthog from 'posthog-js';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { NavigateFunction, useLocation } from 'react-router-dom';
import { ANALYTICS_EVENTS } from 'src/analytics/events';
import { dataService, LegalResearchDatabaseResult } from 'src/services/dataService';
import {
  findResearchInVersionMap,
  groupResearchesByVersion,
  type LegalResearch,
  mapDatabaseRowToResearch,
} from 'src/services/researchMapper';
import { sendNotificationEmail } from 'src/utils/api';
import { getResearchStatusColor, getResearchStatusIconData } from 'src/utils/statusMappings';
import { getCurrentUser, onAuthStateChange } from 'src/utils/supabase';
import { normalizeText } from 'src/utils/textNormalization';

export type { LegalResearch } from 'src/services/researchMapper';

interface LegalResearchContextType {
  researches: LegalResearch[];
  allResearches: LegalResearch[];
  selectedCase: string;
  setSelectedCase: (citation: string) => void;
  getStatusIcon: (status: string) => React.ReactNode;
  getStatusColor: (status: string) => string;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  getResearchById: (id: string | undefined) => LegalResearch | undefined;
  removeCaseFromResearch: (researchId: string, caseCitation: string, caseTitle?: string) => Promise<void>;
  updateCaseNewStatus: (researchId: string, caseCitation: string) => Promise<void>;
  deleteResult: (resultId: string) => Promise<void>;
  refreshResearches: () => Promise<void>;
  initializeResearches: () => Promise<void>;
  getResearchVersions: (resultGroupId: string) => LegalResearch[];
  navigateToVersion: (
    resultGroupId: string,
    versionIndex: number,
    navigate?: NavigateFunction,
    currentLocationState?: unknown
  ) => string | undefined;
  deletingCaseCitation: string | null;
  setDeletingCaseCitation: (citation: string | null) => void;
}

const LegalResearchContext = createContext<LegalResearchContextType | undefined>(undefined);

export const useLegalResearch = () => {
  const context = useContext(LegalResearchContext);
  if (!context) {
    throw new Error('useLegalResearch must be used within a LegalResearchProvider');
  }
  return context;
};

interface LegalResearchProviderProps {
  children: ReactNode;
}

const STATUS_ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  'check-circle': CheckCircle,
  'trending-up': TrendingUp,
  'alert-triangle': AlertTriangle,
  'file-text': FileText,
};

export const LegalResearchProvider: React.FC<LegalResearchProviderProps> = ({ children }) => {
  const [selectedCase, setSelectedCase] = useState<string>('');
  const [researches, setResearches] = useState<LegalResearch[]>([]);
  const [allResearches, setAllResearches] = useState<LegalResearch[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const initializedRef = React.useRef(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [versionMap, setVersionMap] = useState<Map<string, LegalResearch[]>>(new Map());
  const [, setSelectedVersions] = useState<Map<string, number>>(new Map());
  const [deletingCaseCitation, setDeletingCaseCitation] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  const location = useLocation();

  const researchId = React.useMemo(() => {
    const match = location.pathname.match(/^\/research\/([^/]+)$/);
    return match ? match[1] : undefined;
  }, [location.pathname]);

  useEffect(() => {
    setSelectedCase('SUMMARY');
  }, [researchId]);

  const fetchResearches = useCallback(async (isRefresh = false) => {
    if (!isRefresh) setLoading(true);
    setError(null);
    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('User not authenticated');

      const data = await dataService.legalResearch.getUserResearchResults(user.id);

      const mapped: LegalResearch[] = data
        .map((row: LegalResearchDatabaseResult) => mapDatabaseRowToResearch(row))
        .filter((item: LegalResearch | null): item is LegalResearch => !!item);

      setAllResearches(mapped);

      const { latestResearches, versionMap: versionMapData } = groupResearchesByVersion(mapped);
      setVersionMap(versionMapData);
      setResearches(latestResearches);

      const initialSelectedVersions = new Map<string, number>();
      versionMapData.forEach((versions, groupId) => {
        initialSelectedVersions.set(groupId, versions.length - 1);
      });
      setSelectedVersions(initialSelectedVersions);

      setSelectedCase((prev) => {
        if (prev === '' && mapped.length > 0) return 'SUMMARY';
        return prev;
      });
    } catch {
      posthog?.capture(ANALYTICS_EVENTS.API_ERROR, { action: 'fetch_researches' });
      setError('Failed to fetch legal research results');
    } finally {
      if (!isRefresh) setLoading(false);
    }
  }, []);

  const initializeResearches = useCallback(async () => {
    if (initializedRef.current) return;
    const user = await getCurrentUser();
    if (!user) return;
    initializedRef.current = true;
    await fetchResearches();
    setInitialized(true);
  }, [fetchResearches]);

  useEffect(() => {
    const unsubscribe = onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        initializedRef.current = false;
        setInitialized(false);
        setResearches([]);
        setAllResearches([]);
        setVersionMap(new Map());
        setError(null);
        setLoading(false);
        return;
      }
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
        if (!initializedRef.current) {
          initializedRef.current = true;
          fetchResearches().then(() => setInitialized(true));
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [fetchResearches]);

  React.useEffect(() => {
    if (!initialized) return;

    let isMounted = true;
    let channel: RealtimeChannel | null = null;

    const cleanupChannel = () => {
      if (channel) {
        channel.unsubscribe();
        channel = null;
      }
    };

    const setupSubscription = async () => {
      try {
        cleanupChannel();
        const user = await getCurrentUser();
        if (!user || !isMounted) return;

        channel = await dataService.legalResearch.subscribeToResearchUpdates(user.id, (payload: unknown) => {
          if (isMounted) {
            const p = payload as Record<string, unknown>;
            const eventType = p.eventType || p.event;
            const newRecord = p.new as Record<string, unknown> | undefined;

            if (eventType === 'UPDATE' && newRecord) {
              if (newRecord.status === 'DELETING') setDeletingCaseCitation(null);
              if (newRecord.status === 'COMPLETED') {
                const name =
                  typeof newRecord.research_name === 'string' ? newRecord.research_name : 'Untitled Research';
                sendNotificationEmail('research_complete', name).catch(() => {});
              }
            }

            setRefreshing(true);
            fetchResearches(true).finally(() => {
              if (isMounted) setRefreshing(false);
            });
          }
        });
      } catch {
        cleanupChannel();
      }
    };

    setupSubscription();

    return () => {
      isMounted = false;
      cleanupChannel();
    };
  }, [initialized, fetchResearches]);

  const getStatusIcon = useCallback((status: string) => {
    const data = getResearchStatusIconData(status);
    const IconComponent = STATUS_ICON_MAP[data.iconName];
    return <IconComponent className={`w-4 h-4 ${data.colorClass}`} />;
  }, []);

  const getStatusColor = getResearchStatusColor;

  const getResearchById = useCallback(
    (id: string | undefined) => findResearchInVersionMap(id, versionMap, researches),
    [researches, versionMap]
  );

  useEffect(() => {
    if (researchId) {
      const research = getResearchById(researchId);
      if (research?.result_group_id && typeof research.versionIndex === 'number') {
        const groupId = research.result_group_id;
        const versionIndex = research.versionIndex;
        setSelectedVersions((prev) => {
          if (prev.get(groupId) !== versionIndex) {
            const newMap = new Map(prev);
            newMap.set(groupId, versionIndex);
            return newMap;
          }
          return prev;
        });
      }
    }
  }, [researchId, getResearchById]);

  const removeCaseFromResearch = useCallback(
    async (researchId: string, caseCitation: string, caseTitle?: string) => {
      setError(null);
      setDeletingCaseCitation(caseCitation);

      try {
        await dataService.legalResearch.removeCaseFromResearch(researchId, caseCitation, caseTitle);
        setDeletingCaseCitation(null);
        await fetchResearches(true);
      } catch (err: unknown) {
        const errObj = err as Record<string, unknown> | undefined;
        const errMsg = err instanceof Error ? err.message : '';
        const errCode = errObj?.code as string | undefined;
        const isSupabaseFetchError =
          errMsg.includes('Research result not found') ||
          errCode === 'PGRST116' ||
          (errMsg && !errMsg.includes('Failed to delete case') && !errMsg.includes('Case citation is required'));

        if (isSupabaseFetchError) {
          setDeletingCaseCitation(null);
          await fetchResearches(true);
          return;
        }

        posthog?.capture(ANALYTICS_EVENTS.API_ERROR, { action: 'remove_case' });
        setError('Failed to remove case from research');
        setDeletingCaseCitation(null);
        throw err;
      }
    },
    [fetchResearches]
  );

  const updateCaseNewStatus = useCallback(async (researchId: string, caseCitation: string) => {
    try {
      setError(null);
      await dataService.legalResearch.updateCaseNewStatus(researchId, caseCitation);
      setResearches((prev) => {
        return prev.map((research) => {
          if (research.id !== researchId) return research;

          const updatedCaseSummaries = (research.case_summaries || []).map((summary) => {
            const normalize = (value?: string) => normalizeText(value || '');
            const normalizedCitation = normalize(caseCitation);
            const summaryCitation = normalize(summary?.case_citation?.citation);

            if (summaryCitation === normalizedCitation) {
              return { ...summary, new: false };
            }
            return summary;
          });

          return { ...research, case_summaries: updatedCaseSummaries };
        });
      });
    } catch {
      setError('Failed to update case status');
      throw new Error('Failed to update case status');
    }
  }, []);

  const deleteResult = useCallback(
    async (resultId: string) => {
      try {
        setError(null);
        await dataService.legalResearch.deleteResult(resultId);
        await fetchResearches(true);
      } catch {
        posthog?.capture(ANALYTICS_EVENTS.API_ERROR, { action: 'delete_result' });
        setError('Failed to delete result');
        throw new Error('Failed to delete result');
      }
    },
    [fetchResearches]
  );

  const getResearchVersions = useCallback(
    (resultGroupId: string): LegalResearch[] => versionMap.get(resultGroupId) || [],
    [versionMap]
  );

  const navigateToVersion = useCallback(
    (resultGroupId: string, versionIndex: number, navigate?: NavigateFunction, currentLocationState?: unknown) => {
      const versions = versionMap.get(resultGroupId) || [];
      if (versionIndex >= 0 && versionIndex < versions.length) {
        const selectedVersion = versions[versionIndex];

        setSelectedVersions((prev) => {
          const newMap = new Map(prev);
          newMap.set(resultGroupId, versionIndex);
          return newMap;
        });

        if (navigate && selectedVersion) {
          navigate(`/research/${selectedVersion.id}`, {
            replace: true,
            state: currentLocationState,
          });
        }

        return selectedVersion?.id;
      }
      return undefined;
    },
    [versionMap]
  );

  const refreshResearches = useCallback(async () => {
    await fetchResearches(true);
  }, [fetchResearches]);

  const value = useMemo(
    () => ({
      researches,
      allResearches,
      selectedCase,
      setSelectedCase,
      getStatusIcon,
      getStatusColor,
      loading,
      refreshing,
      error,
      getResearchById,
      removeCaseFromResearch,
      updateCaseNewStatus,
      deleteResult,
      refreshResearches,
      initializeResearches,
      getResearchVersions,
      navigateToVersion,
      deletingCaseCitation,
      setDeletingCaseCitation,
    }),
    [
      researches,
      allResearches,
      selectedCase,
      getStatusIcon,
      getStatusColor,
      loading,
      refreshing,
      error,
      getResearchById,
      removeCaseFromResearch,
      updateCaseNewStatus,
      deleteResult,
      refreshResearches,
      initializeResearches,
      getResearchVersions,
      navigateToVersion,
      deletingCaseCitation,
    ]
  );

  return <LegalResearchContext.Provider value={value}>{children}</LegalResearchContext.Provider>;
};
